import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as NotificationsIcon } from 'src/assets/icons/notifications.svg';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Popper } from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import { ArcBox, ArcButton, ArcIconButton, ArcStack, ArcText, ArcTooltip } from '@src/core/ui';
import { NotificationEntity, NotificationsMenuProps } from '../types';
import { useClickMenu } from '../../useClickMenu';
import { headerIconTriggerSx } from '../../trigger-sx';
import { useHoverMenu } from '../useHoverMenu';
import NotificationItem from '../notification-item';
import MoreMenuPanel from '../more-menu-panel';

const DROPDOWN_PREVIEW_COUNT = 8;
const BADGE_MAX_COUNT = 99;

const surfaceSx: SxProps<Theme> = {
  width: 320,
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 4,
  overflow: 'hidden',
};

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({ notifications, badgeCount, onOpen, onViewAll, onMarkAllAsRead, onItemClick }) => {
  const { t } = useTranslation();
  const dropdown = useClickMenu();
  const more = useHoverMenu();

  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const moreRef = React.useRef<HTMLDivElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const closeAll = React.useCallback(() => {
    more.close();
    dropdown.close();
  }, [more.close, dropdown.close]);

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!dropdown.open) onOpen();
    setTooltipOpen(false);
    more.close();
    dropdown.toggle(event);
  };

  React.useEffect(() => {
    if (!dropdown.open) return;

    const isInside = (target: Node) =>
      [dropdown.anchorEl, dropdownRef.current, moreRef.current].some((node) =>
        node?.contains(target),
      );

    const onPointerDown = (event: MouseEvent) => {
      if (isInside(event.target as Node)) return;
      closeAll();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAll();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [dropdown.open, dropdown.anchorEl, closeAll]);

  const handleMarkAllAsRead = () => {
    onMarkAllAsRead();
    more.close();
  };

  const handleViewAll = () => {
    closeAll();
    onViewAll();
  };

  const handleItemClick = (notification: NotificationEntity) => {
    if (onItemClick(notification)) closeAll();
  };

  const badgeLabel = badgeCount > BADGE_MAX_COUNT ? `${BADGE_MAX_COUNT}+` : badgeCount;
  const showViewAll = notifications.length > DROPDOWN_PREVIEW_COUNT;

  return (
    <>
      <ArcStack
        onClick={handleTriggerClick}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}>
        <ArcTooltip
          title={t('notifications.title')}
          open={tooltipOpen && !dropdown.open}
          disableHoverListener
          disableFocusListener
          disableTouchListener>
          <ArcIconButton
            size='small'
            aria-haspopup='menu'
            aria-expanded={dropdown.open}
            aria-label={t('notifications.title')}
            sx={[
              headerIconTriggerSx(dropdown.open),
              {
                color: dropdown.open ? 'common.white' : 'text.primary',
                '& path': { stroke: 'currentColor' },
              },
            ]}>
            <ArcStack width={20} minWidth={20} position='relative'>
              <NotificationsIcon width={20} height={20} aria-hidden />
              {badgeCount > 0 && (
                <ArcBox
                  position='absolute'
                  top={-6}
                  right={-8}
                  width={16}
                  height={16}
                  borderRadius='50%'
                  bgcolor='error.main'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'>
                  <ArcText.caption
                    fontSize={10}
                    fontWeight={500}
                    lineHeight={1}
                    letterSpacing={0}
                    textTransform='uppercase'
                    color='common.white'>
                    {badgeLabel}
                  </ArcText.caption>
                </ArcBox>
              )}
            </ArcStack>
          </ArcIconButton>
        </ArcTooltip>
      </ArcStack>

      <Popper
        ref={dropdownRef}
        open={dropdown.open}
        anchorEl={dropdown.anchorEl}
        placement='bottom-end'
        sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1 }}>
        <ArcStack sx={{ pt: '8px' }}>
          <ArcStack sx={surfaceSx}>
            <ArcStack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ px: 2, py: 1.25, borderBottom: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.12)}` }}>
              <ArcText.h4 fontSize={16} fontWeight={600}>
                {t('notifications.title')}
              </ArcText.h4>
              <ArcStack onMouseEnter={more.openMenu} onMouseLeave={more.scheduleClose}>
                <ArcIconButton size='small' aria-haspopup='menu' aria-expanded={more.open}>
                  <MoreVertIcon sx={{ fontSize: 18, color: 'common.white' }} />
                </ArcIconButton>
              </ArcStack>
            </ArcStack>

            <ArcStack>
              {notifications.length === 0 ? (
                <ArcStack alignItems='center' sx={{ px: 2, py: 3 }}>
                  <ArcText.h5 fontSize={11} color='text.secondary'>
                    {t('notifications.empty')}
                  </ArcText.h5>
                </ArcStack>
              ) : (
                notifications.slice(0, DROPDOWN_PREVIEW_COUNT).map((notification) => (
                  <NotificationItem key={notification.id} notification={notification} onClick={handleItemClick} />
                ))
              )}
            </ArcStack>

            {showViewAll ? (
              <ArcStack sx={{ marginLeft: 'auto', marginRight: '5px' }}>
                <ArcButton
                  variant='text'
                  fullWidth
                  onClick={handleViewAll}
                  sx={{
                    marginLeft: 'auto',
                    py: 1.25,
                    textTransform: 'none',
                    color: 'white',
                    fontSize: 10,
                    fontWeight: 400,
                  }}>
                  {t('notifications.viewAll')}
                  <ArrowForwardIcon sx={{ fontSize: 11, ml: '4px' }} />
                </ArcButton>
              </ArcStack>
            ) : null}
          </ArcStack>
        </ArcStack>
      </Popper>

      <Popper
        ref={moreRef}
        open={more.open && Boolean(more.anchorEl)}
        anchorEl={more.anchorEl}
        placement='bottom-end'
        sx={{ zIndex: (theme) => theme.zIndex.tooltip + 2 }}
        onMouseEnter={() => more.openMenu()}
        onMouseLeave={more.scheduleClose}>
        <ArcStack sx={{ pt: '6px' }}>
          <MoreMenuPanel
            onMarkAllAsRead={handleMarkAllAsRead}
            onNotificationSettings={more.close} />
        </ArcStack>
      </Popper>
    </>
  );
};

export default NotificationsMenu;
